import React from 'react';
import { INavigationItemProps, NavigationItem } from '../Navigation/NavigationItem';
import { IHasTestData } from '../../test-utilities/IHasTestData';
import { StyledCard } from '../Card/Card.Styled';
import styled from 'styled-components';
import { BoldButtonText, ButtonText } from '../Typography';
import { mergeTests } from '../../test-utilities/mergeTests';
import type { FCWithChildren } from '../../../types/FCWithChildren';

export interface ITabItemProps extends INavigationItemProps {
  icon?: React.ReactNode;
}

interface ITabsProps extends IHasTestData {
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  tabs: ITabItemProps[];
  activeTab: ITabItemProps;
}

export const Tabs: FCWithChildren<ITabsProps> = ({ prefix, suffix, tabs, activeTab, children, ...rest }) => (
  <div>
    <TabsRow>
      {prefix ? (
        <PrefixStyled>
          <TabHolderStyled>{prefix}</TabHolderStyled>
        </PrefixStyled>
      ) : null}
      {tabs.map((tab) => (
        <React.Fragment key={tab.text}>
          {tab === activeTab ? (
            <ActiveTabStyled {...tab}>
              {tab.icon ? (
                <>
                  <TabIconStyled>{tab.icon}</TabIconStyled>
                  {tab.text}
                </>
              ) : null}
            </ActiveTabStyled>
          ) : (
            <TabStyled {...tab}>
              {tab.icon ? (
                <>
                  <TabIconStyled>{tab.icon}</TabIconStyled>
                  {tab.text}
                </>
              ) : null}
            </TabStyled>
          )}
        </React.Fragment>
      ))}
      {suffix ? (
        <SuffixStyled>
          <TabHolderStyled>{suffix}</TabHolderStyled>
        </SuffixStyled>
      ) : null}
    </TabsRow>
    <TabContentStyled
      isCollapsed={false}
      isCollapsible={false}
      hideOverflow={false}
      height={0}
      data-test-id={mergeTests(rest['data-test-id'], 'wrapper')}
    >
      {children}
    </TabContentStyled>
  </div>
);

const TabsRow = styled.div`
  display: flex;
  border-bottom: 1px solid ${(props) => props.theme.colors.defaultContentBorder};
  align-items: flex-end;
  & > * {
    margin-left: ${(props) => props.theme.spacing.spacing4};
  }
  & > *:first-child {
    margin-left: ${(props) => props.theme.spacing.spacing4};
  }
`;

const TabStyled = styled(ButtonText.withComponent(NavigationItem))`
  position: relative;
  color: ${(props) => props.theme.colors.darkGray};
  border: 1px solid ${(props) => props.theme.colors.defaultContentBorder};
  border-radius: 10px 10px 0 0;
  border-bottom: 1px solid ${(props) => props.theme.colors.defaultBackground};
  background-color: ${(props) => props.theme.colors.gray};
  margin-bottom: -1px;
  min-width: 150px;
  justify-content: center;
  :hover,
  :focus {
    border-bottom: 1px solid ${(props) => props.theme.colors.defaultBackground};
  }
`;

const ActiveTabStyled = styled(BoldButtonText.withComponent(NavigationItem))`
  position: relative;
  color: ${(props) => props.theme.colors.primary};
  border: 1px solid ${(props) => props.theme.colors.defaultContentBorder};
  border-radius: 10px 10px 0 0;
  border-bottom: 1px solid ${(props) => props.theme.colors.defaultContentBackground};
  background-color: ${(props) => props.theme.colors.defaultContentBackground};
  margin-bottom: -1px;
  min-width: 150px;
  justify-content: center;
  :hover {
    background-color: ${(props) => props.theme.colors.defaultContentBackground};
    border-bottom: 1px solid ${(props) => props.theme.colors.defaultContentBackground};
  }
`;

const TabHolderStyled = styled.div`
  margin-left: ${(props) => props.theme.spacing.spacing5};
  margin-right: ${(props) => props.theme.spacing.spacing5};
`;

const TabContentStyled = styled(StyledCard)`
  border-top: 0;
`;
const SuffixStyled = styled.div`
  justify-self: flex-end;
  margin-left: auto;
  margin-bottom: ${(props) => props.theme.spacing.spacing2};
`;
const PrefixStyled = styled.div`
  margin-bottom: ${(props) => props.theme.spacing.spacing2};
`;

const TabIconStyled = styled.div`
  position: absolute;
  width: ${(props) => props.theme.spacing.spacing5};
  height: ${(props) => props.theme.spacing.spacing5};
  top: ${(props) => `${props.theme.spacing.spacing4}`};
  right: ${(props) => `${props.theme.spacing.spacing4}`};
`;
